import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router';
import { theme, btnSecondary } from '../theme';
import { APP_VERSION } from '../utils/version';
// v0.17.2 — PWA install pill in the header (auto-hides when not installable).
import PwaInstallButton from './PwaInstallButton.jsx';
import DropPinWordmark from './DropPinWordmark.jsx';
import ThemeToggle from './ThemeToggle.jsx';
// v0.17.3 — Offline banner + outbox status.
import OfflineBanner from './OfflineBanner.jsx';
// v0.207.4 — a floor-scan upload keeps running once its picker is closed, so
// it needs somewhere to be VISIBLE from. Work that carries on behind a closed
// screen and shows nothing is worse than work that stops.
import ScanUploadBar from './ScanUploadBar.jsx';
// v0.267.0 — the office's comments not emailed yet, Send update, and the
// leave-the-project check. On every page of a project, staff only.
import UnsentUpdatesBar from './UnsentUpdatesBar.jsx';
import { scanVideoQueue } from '../utils/scanVideoQueue';
import { STORE_BUILD } from '../utils/preview';
// v0.26.0 — Auth gate + top-right user menu. Login screen renders in
// place of the children when no user is signed in. Once signed in, the
// header gets a user pill on the right with full account details + Sign out.
import LoginScreen from '../screens/LoginScreen';
// v0.57.0 - branded splash shown during the silent suite SSO sign-in.
import SsoSplash from './SsoSplash';
import UserMenu from './UserMenu';
import { getCurrentUser, clearCurrentUser, signOutServer, signInNeedsCheck, SSO_AUTOLOGIN } from '../utils/user';
// v0.246.0 — one sign-in across the suite: not signed in here → ask Synerflo ID once.
import { cameBackToSignIn, shouldAskSuite, askSuite } from '../utils/suiteSignIn';
// 2026-10-19 — the server says a sign-in has ended: one way out (endSession).
import { checkSession, noteSessionEnded, sessionResumed, SESSION_ENDED_EVENT } from '../utils/sessionEnd';
// v0.x — Capacitor native shell: silent suite SSO reads the shared
// .insitebg.com.au cookie, which can never ride from capacitor://localhost.
// The wrapped app skips the attempt (and its splash) and goes straight to
// the login screen; auth there rides the bearer token instead. On the web
// isNativeShell() is false and behaviour is unchanged.
import { isNativeShell } from '../utils/nativeShell';
// v0.43.0 — Detect subbie sessions and bounce them to /my-items.
// v0.56.0 — a CLIENT hand-off also holds a contact session but must NOT be
//           bounced; it stays in the shell on the Locations page.
import {
  hasContactSession, isClientContactSession, isSubbieContactSession, getContactSession, clearContactSession,
  endStaleContactSession, contactViewOverOffice,
} from '../utils/contact-session';

// A cache checked before the app opens on it opens anyway after this long
// without an answer (one bar of reception on site).
const CHECK_FIRST_CAP_MS = 4000;

// Wraps every authenticated screen. Top bar with logo + nav, body area
// hosts the route. Designed to feel like a SaaS dashboard from day one
// so the muscle memory carries through to v1.0.
//
// v0.7.1 - In-project tagline strip. On any /projects/<id>/* route the
//          three-step product motto sits as a sub-header so the workflow
//          (drop a plan, drop a pin, send a report) is always visible.
//
// v0.56.0 — TWO KINDS of contact session now reach the shell:
//   • SUBBIE  (no-account subcontractor, magic-link) -> bounced to /my-items
//     (they only ever see the items assigned to them). Unchanged behaviour.
//   • CLIENT  (InSpec client-portal hand-off, view:'projects') -> stays in
//     the shell and sees the Locations page (their sites). This is the fix
//     for "a client lands on the subbie My-items view".
//   A client contact session is NOT an InSite-ID user, so the admin nav
//   (Contacts / Settings, the create actions) is hidden for them — they get
//   a read-only Locations experience. Genuine InSite staff/owners (an
//   insite_sid session, no contact session) are unaffected: full app.
export default function AppShell({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isInProject = /^\/projects\/[^/]+/.test(location.pathname);

  // v0.140.0 — FULL-BLEED SCREENS. The 1200px reading column is right for lists
  // and forms and wrong for a drawing: on a wide monitor it threw away a third
  // of the screen and left the plan sitting in a small box (Brett: "droppin plan
  // is still in a small window. any white space should be plan"). A screen whose
  // whole job is a canvas takes the window. Data, not scattered conditionals —
  // add a pattern here to opt another screen in.
  const FULL_BLEED_ROUTES = [/^\/projects\/[^/]+\/plan(\/|$)/];
  const fullBleed = FULL_BLEED_ROUTES.some((re) => re.test(location.pathname));

  // v0.336.0 — before anything reads the session kind: a client / subbie
  // contact session left over from an earlier visit on a device that holds an
  // office sign-in is ended, so the office is never treated as that contact
  // (utils/contact-session endStaleContactSession). Once, on open.
  useState(() => endStaleContactSession({ officeUser: getCurrentUser() }));
  const [, setViewTick] = useState(0);

  // v0.56.0 — session kind flags. A client contact-session is allowed to
  // roam the read-only Locations surface; a subbie is confined to My items.
  const clientContact = isClientContactSession();
  const subbieContact = isSubbieContactSession();

  // v0.43.0 / v0.56.0 — Subbie-session redirect. ONLY a genuine subbie
  // (no-account subcontractor) is bounced to /my-items. A CLIENT contact
  // session stays put so it can view the Locations page.
  useEffect(() => {
    if (subbieContact && location.pathname !== '/my-items') {
      navigate('/my-items', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, subbieContact]);

  // v0.56.0 — a CLIENT contact session that lands anywhere other than the
  // Locations surface (e.g. '/', or a deep admin route it can't use) is
  // sent to /projects. It has no insite_sid, so admin-only routes would
  // 401 — /projects is the one page built to run on the contact cookie.
  useEffect(() => {
    if (clientContact) {
      const onLocations = location.pathname === '/projects'
        || location.pathname.startsWith('/projects/');
      if (!onLocations) navigate('/projects', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, clientContact]);

  // v0.26.0 — Auth gate. If no session, render LoginScreen and don't
  // mount the children (so no API calls happen before sign-in). On
  // sign-out, clear and re-render the login screen.
  //
  // 2026-10-19 — A CACHE THE SERVER HAS NOT VOUCHED FOR LATELY IS CHECKED
  // FIRST. Brett opened a DropPin report from InSpec: the window still held
  // his name from an old visit, so the report opened and asked for its pins
  // before anything checked the sign-in; the refusals came up as an error, the
  // sign-in screen flashed, and the suite signed him straight back in ("you
  // shouldnt show the message then make it disappear as it makes it look like
  // it doesnt have good security"). Such a cache (utils/user signInNeedsCheck)
  // now waits behind the "Signing you in" screen while the session is asked
  // for — never longer than CHECK_FIRST_CAP_MS, so one bar of reception still
  // opens the app on what this device holds (offline-first).
  const checkFirst = () => SSO_AUTOLOGIN && !isNativeShell() && !hasContactSession()
    && signInNeedsCheck(getCurrentUser());
  const [user, setUser] = useState(() => (checkFirst() ? null : getCurrentUser()));
  // With no cached user (or one being checked first) and no contact session,
  // the branded splash shows while the session is checked and (signed out)
  // Synerflo ID is asked — so the sign-in screen never flashes up on the way.
  const [ssoTrying, setSsoTrying] = useState(
    () => SSO_AUTOLOGIN && !isNativeShell() && !hasContactSession() && (!getCurrentUser() || checkFirst())
  );
  // The page is on its way to Synerflo ID: nothing else changes the screen.
  const leaving = useRef(false);

  // 2026-10-19 — ONE WAY A SIGN-IN ENDS. Whoever finds it — the check on open,
  // a tab shown again, another tab, or a request the server refused
  // (utils/sessionEnd) — the screens go at once behind the splash and the
  // suite is asked (signed in on any suite app → straight back in, nothing to
  // type), or, where it may not be asked, the sign-in screen. Failures from
  // the screens being left stay quiet until a sign-in (alertFailure, Toaster).
  const endSession = useCallback((mayAsk = true) => {
    if (leaving.current) return;
    noteSessionEnded();
    clearCurrentUser();
    if (mayAsk && SSO_AUTOLOGIN && !isNativeShell() && shouldAskSuite()) {
      leaving.current = true;
      setSsoTrying(true);
      setUser(null);
      askSuite(); // the page is leaving; the splash stays up until it does
      return;
    }
    setSsoTrying(false);
    setUser(null);
  }, []);

  // Ask the server who is signed in and act on the answer; offline keeps what
  // is on screen.
  const recheck = useCallback(async () => {
    if (leaving.current || hasContactSession()) return;
    const me = await checkSession();
    if (leaving.current || me.offline) return;
    if (me.signedOut) endSession();
    else if (me.user) { sessionResumed(); setUser(me.user); }
  }, [endSession]);

  useEffect(() => {
    // Pick up session changes from other tabs.
    const onStorage = (e) => {
      if (leaving.current) return;
      // 2026-10-19 — another tab signed in (or out). Signed out there, this
      // tab shows the sign-in screen without asking the server or the suite:
      // the sign-out may still be on its way, and an answer from before it
      // landed would sign both tabs back in.
      if (e.key === 'droppin.user.v1') {
        const u = getCurrentUser();
        if (u) { sessionResumed(); setUser(u); }
        else if (hasContactSession()) setUser(null);
        else endSession(false);
      }
      // 2026-09-14 — a client session signed out in one tab is gone in every tab:
      // the shell reads the stored identity on render, so a re-render is enough.
      if (e.key === 'droppin.contact.v1') setUser(getCurrentUser());
    };
    const onEnded = () => endSession();
    window.addEventListener('storage', onStorage);
    window.addEventListener(SESSION_ENDED_EVENT, onEnded);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(SESSION_ENDED_EVENT, onEnded);
    };
  }, [endSession]);
  // v0.48.0 - verify the cookie session against the API on mount. The
  // localStorage user is only a UI cache now; the real session is the
  // httpOnly InSite-ID cookie (set by /api/auth-login). If the API says
  // 401/403 the cookie is gone or expired - drop to the login screen.
  // Network errors keep the cached session (offline-first PWA).
  //
  // v0.56.0 — SKIP this for a CLIENT contact session. Such a user has NO
  // insite_sid cookie, so fetchMe() would 401 and (pre-fix) drop them to
  // the LoginScreen. Their auth is the droppin_contact cookie; the client
  // Locations page authenticates with that. We only verify the InSite-ID
  // session for genuine staff/owner (non-contact) sessions.
  useEffect(() => {
    if (hasContactSession()) return; // subbie or client — not an insite_sid user
    let cancelled = false;
    // A cache checked first (above) opens the app if the answer is slow.
    const cap = ssoTrying && getCurrentUser() ? setTimeout(() => {
      if (cancelled || leaving.current) return;
      setSsoTrying(false);
      setUser((u) => u || getCurrentUser());
    }, CHECK_FIRST_CAP_MS) : null;
    (async () => {
      // v0.246.0 — ONE SIGN-IN ACROSS THE SUITE. The session first; signed out
      // here, Synerflo ID is asked once (utils/suiteSignIn): signed in on any
      // suite app → back signed in; nobody → back with ?signin=1 and the
      // sign-in screen. Never twice in a minute per tab, never straight after
      // coming back, never from the store app (no browser to pass through).
      const cameBack = cameBackToSignIn();
      const me = await checkSession();
      if (cancelled) return;
      if (cap) clearTimeout(cap);
      if (me.signedOut) { endSession(!cameBack); return; }
      setSsoTrying(false);
      if (me.offline) { setUser((u) => u || getCurrentUser()); return; }
      if (me.user) { sessionResumed(); setUser(me.user); }
    })();
    return () => { cancelled = true; if (cap) clearTimeout(cap); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // 2026-09-14 — A TAB LEFT OPEN RE-CHECKS ITS SESSION WHEN IT COMES BACK.
  // Brett: "There is another tab open logged into Brett" — it was not: its
  // InSite-ID cookie had expired days before, and a tab only verified the
  // session on mount, so it kept showing his name over lists every request
  // was refusing (a 401 on the reports list reads as "no reports"). When the
  // tab is shown again the session is asked for; an expired one drops to the
  // sign-in screen instead of a page of cached nothing. Offline keeps the
  // cached session, as on mount; a contact session is not an InSite-ID one.
  useEffect(() => {
    // 2026-10-19 — through recheck: an ended sign-in goes the one way above
    // (the suite asked behind the splash), not straight to the sign-in form.
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || hasContactSession() || !getCurrentUser() || leaving.current) return;
      recheck();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [recheck]);
  // v0.55.0 — Post-login landing. The CLIENT and InSite STAFF should
  // land on the Locations page (their sites + reports), not the Home
  // hero. Subbies are already bounced to /my-items by the effect above;
  // this only moves an authenticated admin user who lands on '/'.
  useEffect(() => {
    if (user && !hasContactSession() && location.pathname === '/') {
      navigate('/projects', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.pathname]);

  // v0.56.0 — a CLIENT contact session renders the shell WITHOUT an
  // InSite-ID user (there is none). The Locations page reads the client's
  // sites from the contact-cookie endpoint. For a genuine staff/owner we
  // keep the original auth gate: no user -> LoginScreen.
  // The branded splash while the session is checked and Synerflo ID asked;
  // then the app (a user) or the sign-in screen.
  if (SSO_AUTOLOGIN && ssoTrying && !user && !clientContact) {
    return <SsoSplash />;
  }
  if (!user && !clientContact) {
    return <LoginScreen onSignedIn={(u) => { sessionResumed(); setUser(u); }} />;
  }

  // Display name in the header pill: the InSite-ID user for staff, or the
  // client's contact identity for a hand-off client.
  const contact = clientContact ? (getContactSession()?.contact || null) : null;
  const headerUser = user || (contact ? {
    name: contact.name || contact.email,
    email: contact.email,
    company: contact.company,
    role: 'Client',
  } : null);

  const signOut = () => {
    // 2026-09-14 — SIGN OUT ENDS WHICHEVER SESSION IS ON SCREEN. Brett: "that
    // screen wouldnt let me sign out." His PC was in a client hand-off session
    // (the header pill said Nick); this cleared only the OWNER session — the
    // InSite-ID cookie and its cached user — and left the droppin_contact cookie
    // and the stored client identity alone, so the shell re-rendered straight
    // back into the client's view as if nothing had happened. A client or
    // subbie session is ended the way it was made: its cookie cleared
    // server-side (fire-and-forget, anonymous by design) and its stored
    // identity forgotten. Both kinds are cleared when both are present.
    if (hasContactSession()) {
      try { fetch('/api/contact-logout', { method: 'POST', credentials: 'same-origin' }).catch(() => {}); } catch (_e) { /* best-effort */ }
      clearContactSession();
    }
    signOutServer(); // fire-and-forget: clears the httpOnly cookie server-side
    noteSessionEnded(); // what the screens being left still had in flight stays quiet
    clearCurrentUser();
    setUser(null);
    navigate('/');
  };

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, color: theme.text }}>
      {/* v0.45.21 — Brett: "Make the Droppin logo a bit bigger and move
          over to the left." Header padding-left reduced 24px → 12px so
          the logo hugs the left edge; logo square + wordmark scale up. */}
      <header className="ss-appbar" style={{
        minHeight: 60,
        background: 'var(--chrome-grad)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        padding: '0 24px 0 12px',
        boxShadow: '0 1px 0 rgba(0,0,0,0.1)',
      }}>
        <div
          onClick={() => navigate(clientContact ? '/projects' : '/')}
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          {/* v0.45.24 — Brett: "Delete this logo. make the other one a bit
              bigger." The orange brand square + pin emoji was redundant
              with the wordmark (which already has a pin as the i). Square
              removed; wordmark scaled up 20 → 26. */}
          <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.01em' }}>
            <DropPinWordmark />
          </div>
        </div>

        {/* v0.56.0 — a CLIENT hand-off gets a minimal nav (Locations only);
            the admin nav (Contacts / Settings / Home) is InSite-ID-only. */}
        <nav className="ss-nav" style={{ display: 'flex', gap: 4, marginLeft: 32 }}>
          {clientContact ? null : (
            <>
              <NavItem to="/">Home</NavItem>
              <NavItem to="/projects">Locations</NavItem>
              {/* v0.39.0 — tenant Contacts master list.
                  v0.45.20 — Reverted v0.44.0 "Companies" label. The rows
                  are people (name/email/phone) who belong to a company,
                  not companies as the unit. Back to Contacts. */}
              <NavItem to="/contacts">Contacts</NavItem>
              {/* v0.42.0 — tenant brand settings. */}
              <NavItem to="/settings">Settings</NavItem>
            </>
          )}
        </nav>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <PwaInstallButton />
          {/* v0.26.0 — User pill (top-right): account details, theme + revision, Sign out. */}
          {headerUser && <UserMenu user={headerUser} onSignOut={signOut} version={APP_VERSION} />}
        </div>
      </header>

      {/* v0.17.3 — Offline status strip. Auto-hides when online and
          queue is empty. Sits between the header and the rest of the
          shell so it's always visible without taking space when idle. */}
      <OfflineBanner />

      {/* v0.336.0 — a contact view on a device that holds an office sign-in is
          never silent: it says whose view this is, with the way back. */}
      {contactViewOverOffice(getCurrentUser()) && (() => {
        const s = getContactSession() || {};
        const who = (s.contact && (s.contact.name || s.contact.email)) || 'a contact';
        const office = getCurrentUser() || {};
        const kindWord = isClientContactSession() ? 'client' : 'subcontractor';
        // Every screen reads the view as it draws, so the way back is a clean
        // reload of the app as the office (the contact cookie cleared first,
        // never waited on for more than a moment).
        const back = async () => {
          clearContactSession();
          setViewTick((t) => t + 1);
          try {
            await Promise.race([
              fetch('/api/contact-logout', { method: 'POST', credentials: 'same-origin' }).catch(() => {}),
              new Promise((r) => setTimeout(r, 1500)),
            ]);
          } catch (_e) { /* best-effort */ }
          window.location.replace('/');
        };
        return (
          <div role="status" data-contact-view={kindWord} style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, columnGap: 12,
            padding: '8px 16px', background: 'var(--brand-50)', borderBottom: `1px solid ${theme.border}`, borderLeft: `4px solid ${theme.warn}`,
            color: theme.text, fontSize: 13,
          }}>
            <span style={{ minWidth: 0 }}>
              <strong>{kindWord === 'client' ? 'Client view' : 'Subcontractor view'}</strong>
              {' — you are seeing DropPin as '}{who}{'. Live, Pause and the office tools are off in this view.'}
            </span>
            <button type="button" data-contact-view-back onClick={back} style={{ ...btnSecondary, fontSize: 12, padding: '4px 10px', whiteSpace: 'nowrap' }}>
              {'Back to ' + (office.name || office.email || 'my sign-in')}
            </button>
          </div>
        );
      })()}

      {isInProject && !clientContact && !subbieContact && /^\/projects\/\d+(\/|$)/.test(location.pathname) && (
        <UnsentUpdatesBar projectId={location.pathname.match(/^\/projects\/(\d+)/)[1]} />
      )}

      {/* v0.7.1 - product-motto sub-header.
          v0.45.28 — Brett (screenshot, red marker straight through
          this banner + the PlanScreen h1): "i think we are wasting
          space at the top of the screen. Lets make some room."
          Banner permanently hidden. Marketing motto lives on the
          HomeScreen hero; project pages now start with their own
          header right under the nav. ~55px reclaimed. */}

      <main className="ss-main" style={fullBleed
        ? { maxWidth: 'none', margin: 0, padding: '14px 16px 16px' }
        : { maxWidth: 1200, margin: '0 auto', padding: '28px 24px' }}>
        {children}
      </main>

      {/* Tapping it goes back to the job the clip belongs to, which is where
          the picker lives. The queue knows which job that is; nothing else in
          the shell has to. */}
      {/* v0.249.0 — not in the App Store build, which has no floor scan
          (utils/preview.js). */}
      {!STORE_BUILD && (
      <ScanUploadBar onOpen={() => {
        const q = scanVideoQueue().get();
        const pid = (q.find(x => x.projectId != null) || {}).projectId;
        if (pid != null) navigate(`/projects/${pid}/register?scanVideos=1`);
      }} />
      )}
    </div>
  );
}

function NavItem({ to, children }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      style={({ isActive }) => ({
        padding: '8px 14px',
        borderRadius: 6,
        fontSize: 13,
        fontWeight: 500,
        textDecoration: 'none',
        color: isActive ? 'white' : 'rgba(255,255,255,0.65)',
        background: isActive ? 'rgba(255,255,255,0.08)' : 'transparent',
      })}
    >{children}</NavLink>
  );
}
